/**
 * Instance form state for AddToCollectionModal, with per-context remembered fields.
 */
import { usePrevious } from '@uidotdev/usehooks';
import { useCallback, useEffect, useRef, useState } from 'react';

import useUserSettings from '@/features/settings/hooks/api';

import type { CardInstanceFormData } from '../components/CardInstanceForm';
import useFormMemory, { type FormMemoryValues } from './useFormMemory';
import useRememberedFields, { type RememberedFieldKey } from './useRememberedFields';

export interface UseAddCardFormOptions {
  isOpen: boolean;
  defaultFolderId?: string | null;
  setCode?: string | null;
}

const getDefaultFormData = (settings: {
  currency: 'USD' | 'EUR';
  defaultCondition?: string;
  defaultLanguage?: string;
}): CardInstanceFormData => ({
  quantity: 1,
  condition: (settings.defaultCondition as CardInstanceFormData['condition']) ?? 'NM',
  finish: 'nonfoil',
  language: (settings.defaultLanguage as CardInstanceFormData['language']) ?? 'en',
  folderId: null,
  purchasePrice: null,
  purchaseCurrency: settings.currency,
  purchaseDate: null,
  notes: '',
  isAltered: false,
  isSigned: false,
  isPlaytest: false,
});

const getContextKey = (setCode?: string | null, defaultFolderId?: string | null) => {
  if (setCode) return `set:${setCode.toLowerCase()}`;
  if (defaultFolderId && typeof defaultFolderId === 'string') return `folder:${defaultFolderId}`;
  return '_default';
};

export default function useAddCardForm({ isOpen, defaultFolderId, setCode }: UseAddCardFormOptions) {
  const { settings } = useUserSettings();
  const { getMemory, updateMemory, clearFieldFromMemory } = useFormMemory();
  const { rememberedFields, isRemembered, toggleField, resetToDefaults } = useRememberedFields();

  // Captured once per modal open so memory writes stay in one context
  const contextKeyRef = useRef('_default');

  const buildFormData = useCallback((): CardInstanceFormData => {
    const result = getDefaultFormData(settings);
    const memory = getMemory(contextKeyRef.current);

    result.folderId = (isRemembered('folderId') ? memory.folderId : undefined) ?? defaultFolderId ?? null;
    if (isRemembered('condition') && memory.condition) result.condition = memory.condition;
    if (isRemembered('finish') && memory.finish) result.finish = memory.finish;
    if (isRemembered('language') && memory.language) result.language = memory.language;
    if (isRemembered('purchasePrice') && memory.purchasePrice !== undefined) result.purchasePrice = memory.purchasePrice;
    if (isRemembered('purchaseCurrency') && memory.purchaseCurrency) result.purchaseCurrency = memory.purchaseCurrency;
    if (isRemembered('purchaseDate') && memory.purchaseDate) result.purchaseDate = memory.purchaseDate;
    if (isRemembered('notes') && memory.notes) result.notes = memory.notes;
    if (isRemembered('isAltered') && memory.isAltered !== undefined) result.isAltered = memory.isAltered;
    if (isRemembered('isSigned') && memory.isSigned !== undefined) result.isSigned = memory.isSigned;
    if (isRemembered('isPlaytest') && memory.isPlaytest !== undefined) result.isPlaytest = memory.isPlaytest;

    return result;
  }, [settings, getMemory, isRemembered, defaultFolderId]);

  const [formData, setFormData] = useState<CardInstanceFormData>(buildFormData);

  const prevIsOpen = usePrevious(isOpen);
  useEffect(() => {
    if (!isOpen || prevIsOpen) return;
    contextKeyRef.current = getContextKey(setCode, defaultFolderId);
    setFormData(buildFormData());
  }, [isOpen, prevIsOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const resetForm = useCallback(() => setFormData(buildFormData()), [buildFormData]);

  const resetQuantity = useCallback(() => setFormData((prev) => ({ ...prev, quantity: 1 })), []);

  // Not persisted: a printing-specific correction shouldn't overwrite the remembered finish
  const setFinishWithoutMemory = useCallback((finish: CardInstanceFormData['finish']) => setFormData((prev) => ({ ...prev, finish })), []);

  // Persist remembered fields to the context memory on every edit
  const handleFormChange = useCallback(
    (data: CardInstanceFormData) => {
      setFormData(data);
      const valuesToPersist: Partial<FormMemoryValues> = {};
      for (const field of rememberedFields) {
        (valuesToPersist as Record<string, unknown>)[field] = data[field] ?? undefined;
      }
      if (Object.keys(valuesToPersist).length > 0) {
        updateMemory(contextKeyRef.current, valuesToPersist);
      }
    },
    [rememberedFields, updateMemory],
  );

  const handleToggleRemembered = useCallback(
    (key: RememberedFieldKey) => {
      const wasRemembered = isRemembered(key);
      toggleField(key);
      if (!wasRemembered) {
        updateMemory(contextKeyRef.current, { [key]: formData[key] ?? undefined });
      } else {
        clearFieldFromMemory(contextKeyRef.current, key);
      }
    },
    [isRemembered, toggleField, updateMemory, clearFieldFromMemory, formData],
  );

  const contextLabel = setCode ? 'this set' : defaultFolderId ? 'this folder' : 'new cards';

  return {
    formData,
    handleFormChange,
    resetForm,
    resetQuantity,
    setFinishWithoutMemory,
    rememberedFields,
    handleToggleRemembered,
    resetRememberedToDefaults: resetToDefaults,
    contextLabel,
  };
}
